import { Box } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * PartnerLogo - Atomic component for displaying partner/sponsor logos
 *
 * A reusable logo display component that ensures consistent styling and
 * responsive sizing across all partner logos in the application.
 *
 * @param {Object} props - Component props
 * @param {string} props.src - Path to the logo image (required)
 * @param {string} props.alt - Alt text for accessibility (required)
 * @param {Object} [props.size] - Height configuration with responsive values
 * @param {'lazy'|'eager'} [props.loading] - Image loading strategy
 * @param {Object} [props.sx] - Additional MUI sx styling props
 * @returns {JSX.Element} Rendered partner logo
 *
 * @example
 * // Basic usage
 * <PartnerLogo src="/logos/company.png" alt="Company Name" />
 *
 * @example
 * // With responsive sizing
 * <PartnerLogo
 *   src="/logos/company.png"
 *   alt="Company Name"
 *   size={{ height: { xs: '32px', md: '48px' } }}
 * />
 */
function PartnerLogo({ src, alt, size, loading = 'lazy', sx = {} }) {
  // Default size configuration if not provided
  const defaultSize = {
    height: { xs: '32px', md: '40px' },
  };

  // Merge provided size with defaults
  const logoSize = size?.height || defaultSize.height;

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading={loading}
      sx={{
        height: logoSize,
        width: 'auto',
        objectFit: 'contain',
        opacity: 0.95,
        flexShrink: 0,
        transition: 'opacity 0.3s ease',
        '&:hover': {
          opacity: 1,
        },
        ...sx,
      }}
    />
  );
}

PartnerLogo.propTypes = {
  /** Path to the logo image */
  src: PropTypes.string.isRequired,
  /** Alt text for accessibility */
  alt: PropTypes.string.isRequired,
  /** Size configuration object */
  size: PropTypes.shape({
    height: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.object, // For responsive values
    ]),
  }),
  /** Image loading strategy */
  loading: PropTypes.oneOf(['lazy', 'eager']),
  /** Additional MUI sx styling */
  sx: PropTypes.object,
};

export default PartnerLogo;
